import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

import useIsMobile from "../hooks/useIsMobile";

const STOPS = [0, 0.18, 0.82, 1];

// On mobile, the pinned/scroll-scrubbed "cinematic slide" treatment reads as a
// story-style presentation and forces every section to eat a full screen.
// Desktop keeps the pin + parallax; mobile falls back to a normal, lightly
// padded block so the page reads and scans like a standard site.
export default function CinematicSection({
  children,
  zIndex = 10,
  className = "",
  trackHeight = "160vh",
  overflowY,
  fitContent = false,
  staticFlow = false,
}) {
  const isMobile = useIsMobile();
  const trackRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start end", "end start"],
  });

  const scale = useTransform(scrollYProgress, STOPS, [1.02, 1, 1, 0.98]);
  const y = useTransform(scrollYProgress, STOPS, ["4%", "0%", "0%", "-3%"]);

  const overflowClass =
    overflowY === "auto" ? "overflow-y-auto overflow-x-hidden" : "overflow-hidden";

  let trackStyle;
  let innerStyle;
  let innerClassName;

  if (isMobile) {
    trackStyle = { zIndex };
    innerStyle = undefined;
    innerClassName = `relative w-full py-8 ${className}`;
  } else if (staticFlow) {
    // For sections whose content can grow at runtime (e.g. an accordion), a
    // fixed-height pinned track can't accommodate that growth — the sticky
    // box just spills past the track and overlaps whatever follows in the
    // document. Render as a normal auto-height block instead so the page
    // (and anything below it, like the footer) reflows naturally.
    trackStyle = { zIndex };
    innerStyle = undefined;
    innerClassName = `relative w-full ${fitContent ? "py-16" : "py-8"} ${className}`;
  } else {
    trackStyle = { height: trackHeight, zIndex };
    innerStyle = { scale, y };
    innerClassName = `sticky top-0 ${fitContent ? "py-16" : "h-screen"} w-full flex items-center justify-center ${overflowClass} ${className}`;
  }

  return (
    <div ref={trackRef} className="relative" style={trackStyle}>
      <motion.div style={innerStyle} className={innerClassName}>
        {children}
      </motion.div>
    </div>
  );
}
